/**
 * Onboarding.jsx — HealHub New User Onboarding
 *
 * 5-step onboarding flow for new users:
 *  Step 1 — Welcome (personal greeting, what HealHub is)
 *  Step 2 — Meet Saksham (therapy companion intro + first message)
 *  Step 3 — Features tour (diary, quick note, report, community)
 *  Step 4 — Try a Quick Note (first real AI interaction)
 *  Step 5 — You're all set (encouragement + go to dashboard)
 *
 * Completion:
 *  - Sets profileCompleted: true in Firestore (existing flag Dashboard checks)
 *  - Sets onboardingDone: true in Firestore
 *  - Redirects to /dashboard
 *
 * Skip:
 *  - Always visible — goes straight to /dashboard and marks complete
 *
 * New user detection (in Dashboard.jsx):
 *  - Existing logic checks profileCompleted !== true → redirects to /setup-profile
 *  - After setup-profile, redirect to /onboarding instead of /dashboard
 *  - Or: add onboardingDone check in Dashboard.jsx (see comment at bottom)
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate }                 from 'react-router-dom';
import { auth, db }                    from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { authFetch }                   from '../utils/authFetch';
import '../styles/global.css';

// ─── Step definitions ─────────────────────────────────────────────────────
const TOTAL_STEPS = 5;

// Feature cards shown on step 3
const FEATURES = [
  {
    emoji: '📓',
    title: 'Diary',
    desc : 'Write freely. Saksham reads every entry and responds with genuine emotional insight.',
    color: '#5c6bc0',
  },
  {
    emoji: '⚡',
    title: 'Quick Note',
    desc : 'Feeling something right now? Log it in seconds. Builds your mood trend over time.',
    color: '#26a69a',
  },
  {
    emoji: '📊',
    title: 'Wellness Report',
    desc : 'A deep monthly analysis of your patterns, strengths, and growth. Like a mirror for your mind.',
    color: '#7b1fa2',
  },
  {
    emoji: '💬',
    title: 'Chattr',
    desc : 'Anonymous interest-based chats with others who get it. No names, just connection.',
    color: '#e64a19',
  },
  {
    emoji: '🧪',
    title: '12+ Assessments',
    desc : 'Clinically-backed tests for anxiety, depression, burnout, addiction, and more.',
    color: '#2e7d32',
  },
  {
    emoji: '🎯',
    title: 'Focus Room',
    desc : 'A distraction-free space built for concentration and deep work.',
    color: '#1565c0',
  },
];

// Saksham's opening message shown in step 2
const SAKSHAM_INTRO = "Hi there 💙 I'm Saksham — HealHub's mental wellness companion. I'm not a bot with scripted replies. I listen, I reflect, and I try to genuinely understand what you're going through. Whether you're feeling anxious, lost, or just need someone to talk to — I'm here. No judgment, ever.";

export default function Onboarding() {
  const navigate = useNavigate();
  const [step,        setStep]        = useState(1);
  const [userName,    setUserName]    = useState('');
  const [quickNote,   setQuickNote]   = useState('');
  const [noteResult,  setNoteResult]  = useState(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError,   setNoteError]   = useState('');
  const [completing,  setCompleting]  = useState(false);
  const [sakshamTyping, setSakshamTyping] = useState(true);
  const [sakshamText,   setSakshamText]   = useState('');
  const typingTimeout = useRef(null);

  // Get user display name from Firebase Auth
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      const name = user.displayName?.split(' ')[0] || 'there';
      setUserName(name);
    }
  }, []);

  // Typewriter effect for Saksham's intro message on step 2
  useEffect(() => {
    if (step !== 2) return;
    setSakshamTyping(true);
    setSakshamText('');
    let i = 0;
    const tick = () => {
      setSakshamText(SAKSHAM_INTRO.slice(0, i));
      i++;
      if (i <= SAKSHAM_INTRO.length) {
        typingTimeout.current = setTimeout(tick, 18);
      } else {
        setSakshamTyping(false);
      }
    };
    tick();
    return () => clearTimeout(typingTimeout.current);
  }, [step]);

  // ─── Mark onboarding complete in Firestore ──────────────────────────────
  const markComplete = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await setDoc(doc(db, 'users', user.uid), {
        profileCompleted: true,  // existing Dashboard check
        onboardingDone  : true,
        onboardingAt    : serverTimestamp(),
      }, { merge: true });
    } catch { /* non-critical */ }
  };

  const handleComplete = async () => {
    setCompleting(true);
    await markComplete();
    navigate('/dashboard');
  };

  const handleSkip = async () => {
    await markComplete();
    navigate('/dashboard');
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) setStep(s => s + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(s => s - 1);
  };

  // ─── Step 4: Try a quick note ───────────────────────────────────────────
  const handleTryQuickNote = async () => {
    if (!quickNote.trim() || noteLoading) return;
    setNoteLoading(true);
    setNoteError('');
    try {
      const res  = await authFetch('/api/quick-note/analyze', {
        method: 'POST',
        body  : JSON.stringify({ text: quickNote }),
      });
      const data = await res.json();
      if (!res.ok) {
        setNoteError(data.error || 'Something went wrong. Try again.');
        return;
      }
      setNoteResult(data);
    } catch {
      setNoteError('Network error. Please try again.');
    } finally {
      setNoteLoading(false);
    }
  };

  // ─── Shared styles ────────────────────────────────────────────────────────
  const card = {
    background: 'var(--card-bg, #fff)',
    borderRadius: '20px',
    border: '1px solid var(--border-color)',
    padding: '36px 32px',
    maxWidth: '600px',
    width: '100%',
    boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
    animation: 'fadeInUp 0.4s ease',
  };

  const btn = (bg = 'var(--primary-color, #5c6bc0)', color = '#fff') => ({
    padding: '12px 28px',
    background: bg, color,
    border: 'none', borderRadius: '12px',
    fontSize: '0.95rem', fontWeight: 700,
    cursor: 'pointer', transition: 'opacity 0.2s',
  });

  const MOOD_COLORS = {
    happy:'#4caf50', grateful:'#66bb6a', hopeful:'#26a69a', calm:'#42a5f5',
    neutral:'#78909c', tired:'#8d6e63', sad:'#5c6bc0', anxious:'#ef5350',
    overwhelmed:'#e53935', angry:'#d32f2f', crisis:'#1976d2', proud:'#8e24aa',
    excited:'#ff6f00', lonely:'#546e7a',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e8f4fd 0%, #f3e5f5 50%, #e8f5e9 100%)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '20px',
      fontFamily: 'inherit',
    }}>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
      `}</style>

      {/* Progress bar */}
      <div style={{ width: '100%', maxWidth: '600px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 600 }}>
            Step {step} of {TOTAL_STEPS}
          </span>
          <button
            onClick={handleSkip}
            style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '0.82rem' }}
          >
            Skip onboarding →
          </button>
        </div>
        <div style={{ height: '6px', background: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: '4px',
            background: 'linear-gradient(90deg, #5c6bc0, #9c27b0)',
            width: `${(step / TOTAL_STEPS) * 100}%`,
            transition: 'width 0.4s ease',
          }} />
        </div>
      </div>

      {/* ── STEP 1: WELCOME ───────────────────────────────────────────────── */}
      {step === 1 && (
        <div style={card}>
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: '12px' }}>💖🌿</div>
            <h1 style={{ fontSize: '1.9rem', fontWeight: 800, margin: '0 0 10px', color: 'var(--text-color)' }}>
              Welcome to HealHub{userName !== 'there' ? `, ${userName}` : ''}
            </h1>
            <p style={{ color: 'var(--text-secondary-color)', fontSize: '1rem', lineHeight: '1.7', margin: 0 }}>
              A space built for your emotional wellbeing. Not to track you, not to judge you —
              just to listen, support, and help you grow at your own pace.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '28px' }}>
            {[
              ['🤖', 'AI Therapy Companion', 'Saksham listens like a friend, thinks like a therapist'],
              ['📓', 'Personal Diary', 'Private, insightful, always here'],
              ['📊', 'Wellness Reports', 'See your patterns, celebrate your growth'],
              ['🤝', 'Community', 'You\'re never alone on this journey'],
            ].map(([emoji, title, desc]) => (
              <div key={title} style={{
                padding: '14px', borderRadius: '12px',
                background: 'var(--card-bg-secondary, #f8f9fa)',
                border: '1px solid var(--border-color)',
              }}>
                <div style={{ fontSize: '1.4rem', marginBottom: '6px' }}>{emoji}</div>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '3px' }}>{title}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary-color)', lineHeight: '1.4' }}>{desc}</div>
              </div>
            ))}
          </div>

          <button onClick={handleNext} style={{ ...btn(), width: '100%', padding: '14px' }}>
            Let's get started →
          </button>
        </div>
      )}

      {/* ── STEP 2: MEET SAKSHAM ──────────────────────────────────────────── */}
      {step === 2 && (
        <div style={card}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '8px' }}>💬</div>
            <h2 style={{ fontSize: '1.6rem', fontWeight: 800, margin: '0 0 8px' }}>
              Meet Saksham
            </h2>
            <p style={{ color: 'var(--text-secondary-color)', fontSize: '0.9rem', margin: 0 }}>
              Your AI mental wellness companion — built by HealHub
            </p>
          </div>

          {/* Saksham's message with typewriter effect */}
          <div style={{
            background: 'linear-gradient(135deg, #e8f4fd, #f0f4ff)',
            borderRadius: '16px', padding: '20px',
            borderLeft: '4px solid #5c6bc0',
            marginBottom: '24px', minHeight: '120px',
            position: 'relative',
          }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #5c6bc0, #7b1fa2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1rem', flexShrink: 0,
              }}>💙</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#5c6bc0', marginBottom: '6px' }}>
                  Saksham · HealHub AI
                </div>
                <p style={{ margin: 0, lineHeight: '1.7', fontSize: '0.95rem', color: 'var(--text-color)' }}>
                  {sakshamText}
                  {sakshamTyping && (
                    <span style={{ display: 'inline-block', animation: 'pulse 1s infinite', marginLeft: '2px' }}>|</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* What Saksham can help with */}
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '10px', color: 'var(--text-color)' }}>
              What Saksham is trained for:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {['Anxiety', 'Stress', 'Relationships', 'Grief', 'Depression', 'Burnout', 'Self-esteem', 'Mindfulness', 'Personal growth'].map(tag => (
                <span key={tag} style={{
                  padding: '4px 12px', borderRadius: '20px',
                  background: 'var(--card-bg-secondary, #f0f4ff)',
                  border: '1px solid var(--border-color)',
                  fontSize: '0.8rem', color: 'var(--text-color)',
                }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleBack} style={{ ...btn('transparent', 'var(--text-secondary-color)'), border: '1.5px solid var(--border-color)' }}>
              ← Back
            </button>
            <button onClick={handleNext} style={{ ...btn(), flex: 1 }} disabled={sakshamTyping}>
              {sakshamTyping ? 'Saksham is typing...' : 'Continue →'}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: FEATURES TOUR ─────────────────────────────────────────── */}
      {step === 3 && (
        <div style={{ ...card, maxWidth: '680px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>✨</div>
            <h2 style={{ fontSize: '1.6rem', fontWeight: 800, margin: '0 0 8px' }}>
              Everything HealHub offers
            </h2>
            <p style={{ color: 'var(--text-secondary-color)', fontSize: '0.9rem', margin: 0 }}>
              You have access to all of this — most of it free, forever.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '28px' }}>
            {FEATURES.map(f => (
              <div key={f.title} style={{
                padding: '16px', borderRadius: '14px',
                border: `1.5px solid ${f.color}30`,
                background: `${f.color}08`,
                transition: 'transform 0.15s',
              }}>
                <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>{f.emoji}</div>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: f.color, marginBottom: '4px' }}>{f.title}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary-color)', lineHeight: '1.5' }}>{f.desc}</div>
              </div>
            ))}
          </div>

          <div style={{
            padding: '14px 16px', borderRadius: '12px',
            background: '#e8f5e9', border: '1px solid #a5d6a7',
            marginBottom: '20px', fontSize: '0.88rem', color: '#2e7d32',
          }}>
            💙 <strong>Plus plan (₹35/month)</strong> unlocks unlimited diary insights, Saksham chat, and monthly reports.
            The free plan is generous — start there.
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleBack} style={{ ...btn('transparent', 'var(--text-secondary-color)'), border: '1.5px solid var(--border-color)' }}>
              ← Back
            </button>
            <button onClick={handleNext} style={{ ...btn(), flex: 1 }}>
              Try it out →
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4: FIRST QUICK NOTE ──────────────────────────────────────── */}
      {step === 4 && (
        <div style={card}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>⚡</div>
            <h2 style={{ fontSize: '1.6rem', fontWeight: 800, margin: '0 0 8px' }}>
              Your first Quick Note
            </h2>
            <p style={{ color: 'var(--text-secondary-color)', fontSize: '0.9rem', margin: 0 }}>
              How are you feeling right now? Just a few words — Saksham will respond.
            </p>
          </div>

          {!noteResult ? (
            <>
              <textarea
                value={quickNote}
                onChange={e => setQuickNote(e.target.value)}
                placeholder="I'm feeling a little nervous about this new app... or maybe curious? Honestly both."
                rows={4}
                style={{
                  width: '100%', padding: '14px 16px',
                  borderRadius: '12px', border: '1.5px solid var(--border-color)',
                  background: 'var(--input-bg, #f8f9fa)', color: 'var(--text-color)',
                  fontSize: '0.95rem', resize: 'none',
                  boxSizing: 'border-box', lineHeight: '1.6',
                  marginBottom: '12px',
                }}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleTryQuickNote())}
              />

              {noteError && (
                <p style={{ color: '#c62828', fontSize: '0.85rem', marginBottom: '10px' }}>{noteError}</p>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={handleBack} style={{ ...btn('transparent', 'var(--text-secondary-color)'), border: '1.5px solid var(--border-color)' }}>
                  ← Back
                </button>
                <button
                  onClick={handleTryQuickNote}
                  disabled={!quickNote.trim() || noteLoading}
                  style={{ ...btn(), flex: 1, opacity: !quickNote.trim() || noteLoading ? 0.6 : 1 }}
                >
                  {noteLoading ? 'Saksham is reading... 💭' : 'See what Saksham says →'}
                </button>
              </div>

              <button
                onClick={handleNext}
                style={{ width: '100%', marginTop: '10px', background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '0.82rem' }}
              >
                Skip this step →
              </button>
            </>
          ) : (
            <>
              {/* Show the AI response */}
              <div style={{
                padding: '20px', borderRadius: '14px',
                background: noteResult.isCrisis ? '#e3f2fd' : 'var(--card-bg-secondary, #f8f9fa)',
                borderLeft: `4px solid ${MOOD_COLORS[noteResult.mood] || '#5c6bc0'}`,
                marginBottom: '20px',
                animation: 'fadeInUp 0.4s ease',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#5c6bc0' }}>💙 SAKSHAM</span>
                  {noteResult.mood && (
                    <span style={{
                      padding: '2px 10px', borderRadius: '12px', fontSize: '0.72rem',
                      background: MOOD_COLORS[noteResult.mood] || '#5c6bc0', color: '#fff', fontWeight: 600,
                    }}>
                      {noteResult.mood}
                    </span>
                  )}
                </div>
                <p style={{ margin: 0, lineHeight: '1.7', fontSize: '0.95rem' }}>
                  {noteResult.insight}
                </p>
                {noteResult.isCrisis && (
                  <a href="tel:9152987821" style={{
                    display: 'inline-block', marginTop: '12px', padding: '8px 16px',
                    background: '#1976d2', color: '#fff', borderRadius: '8px',
                    textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600,
                  }}>
                    📞 iCall — 9152987821
                  </a>
                )}
              </div>

              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary-color)', marginBottom: '16px', textAlign: 'center' }}>
                This is what every quick note feels like. Your mood is tracked over time to build your wellness report. 💙
              </p>

              <button onClick={handleNext} style={{ ...btn(), width: '100%', padding: '14px' }}>
                Continue →
              </button>
            </>
          )}
        </div>
      )}

      {/* ── STEP 5: YOU'RE ALL SET ────────────────────────────────────────── */}
      {step === 5 && (
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>🌱</div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 800, margin: '0 0 12px' }}>
            You're all set{userName !== 'there' ? `, ${userName}` : ''}!
          </h2>
          <p style={{ color: 'var(--text-secondary-color)', fontSize: '1rem', lineHeight: '1.7', marginBottom: '28px' }}>
            HealHub is here whenever you need it — whether that's writing in your diary,
            chatting with Saksham at 2am, or checking your monthly wellness story.
            This is your space. Take it at your own pace. 💙
          </p>

          {/* Quick action cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '28px' }}>
            {[
              ['💬', 'Chat with Saksham', '/chatbot', '#5c6bc0'],
              ['📓', 'Write in Diary', '/dashboard', '#26a69a'],
              ['🧪', 'Take an Assessment', '/resources', '#7b1fa2'],
              ['🤝', 'Join Community', '/community', '#e64a19'],
            ].map(([emoji, label, href, color]) => (
              <div
                key={label}
                onClick={() => navigate(href)}
                style={{
                  padding: '14px', borderRadius: '12px',
                  border: `1.5px solid ${color}30`,
                  background: `${color}08`, cursor: 'pointer',
                  transition: 'transform 0.15s',
                  fontSize: '0.85rem', fontWeight: 600, color,
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <div style={{ fontSize: '1.6rem', marginBottom: '4px' }}>{emoji}</div>
                {label}
              </div>
            ))}
          </div>

          <button
            onClick={handleComplete}
            disabled={completing}
            style={{ ...btn(), width: '100%', padding: '15px', fontSize: '1rem' }}
          >
            {completing ? 'Setting up your space... 💙' : 'Go to HealHub Dashboard →'}
          </button>
        </div>
      )}
    </div>
  );
}

/*
 * ─── HOW TO WIRE ONBOARDING INTO YOUR APP ────────────────────────────────
 *
 * 1. Add route in App.js:
 *    import Onboarding from './pages/Onboarding';
 *    <Route path="/onboarding" element={<Onboarding />} />
 *
 * 2. After setup-profile completes, redirect to /onboarding instead of /dashboard:
 *    navigate('/onboarding');
 *
 * 3. OR add this check in Dashboard.jsx useEffect (after profileCompleted check):
 *
 *    const snap = await getDoc(doc(db, 'users', user.uid));
 *    if (snap.exists()) {
 *      if (snap.data().profileCompleted !== true) { navigate('/setup-profile'); return; }
 *      if (snap.data().onboardingDone !== true)   { navigate('/onboarding');    return; }
 *    }
 *
 * ─────────────────────────────────────────────────────────────────────────
 */